import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Bell,
  BellOff,
  Check,
  Clock,
  AlertTriangle,
  Briefcase,
  AlertOctagon,
  Activity,
  GitBranch,
  Building2,
  ChevronRight,
  X
} from 'lucide-react';

// Iconos por categoría
const CATEGORY_ICONS = {
  alertas: AlertTriangle,
  casos: Briefcase,
  escalaciones: AlertOctagon,
  monitores: Activity,
  aprobaciones: GitBranch,
  onboarding: Building2,
  sistema: Bell
};

// Colores por prioridad
const PRIORITY_COLORS = {
  low: 'text-slate-500',
  medium: 'text-blue-600',
  high: 'text-amber-600',
  critical: 'text-red-600'
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Load unread count on mount and set up polling
  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const res = await api.get('/notifications/v2/inbox/count');
      setUnreadCount(res.data?.unread_count || 0);
    } catch (error) {
      console.error('Error loading notification count:', error);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications/v2/inbox?limit=5&unread_only=true');
      setNotifications(res.data || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!isOpen) {
      loadNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleMarkAsRead = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await api.post(`/notifications/v2/inbox/${notificationId}/read`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    try {
      await api.post(`/notifications/v2/inbox/${notification.id}/read`);
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {}
    
    setIsOpen(false);
    
    // Navigate to link if available, otherwise to inbox
    if (notification.link) {
      navigate(notification.link);
    } else {
      navigate('/m/notificaciones/inbox');
    }
  };

  const handleViewAll = () => {
    setIsOpen(false);
    navigate('/m/notificaciones/inbox');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleBellClick}
        className="relative text-slate-600 hover:text-indigo-600"
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold text-white bg-red-500 rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-slate-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-2">
              <Bell size={16} className="text-indigo-600" />
              <span className="font-semibold text-slate-900">Notificaciones</span>
              {unreadCount > 0 && (
                <Badge className="bg-red-500 text-white text-xs">{unreadCount}</Badge>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
              <X size={14} />
            </Button>
          </div>

          {/* Content */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                <BellOff size={32} className="mb-2" />
                <p className="text-sm">No hay notificaciones nuevas</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((notification) => {
                  const CategoryIcon = CATEGORY_ICONS[notification.category] || Bell;
                  const priorityColor = PRIORITY_COLORS[notification.priority] || PRIORITY_COLORS.medium;
                  
                  return (
                    <div
                      key={notification.id}
                      className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start gap-3">
                        <CategoryIcon size={16} className={priorityColor} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 line-clamp-1">
                            {notification.title}
                          </p>
                          <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Clock size={10} />
                              {formatDate(notification.created_at)}
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-slate-400 hover:text-green-600"
                          onClick={(e) => handleMarkAsRead(e, notification.id)}
                          title="Marcar como leída"
                        >
                          <Check size={14} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
              onClick={handleViewAll}
            >
              Ver todas las notificaciones
              <ChevronRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
